import { type QueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { JSX } from "react";
import { eligibilityQueryOptions } from "@/app/onboarding/api/get-eligibility";
import { onboardingProgressQueryOptions } from "@/app/onboarding/api/get-onboarding-progress";
import { canEnterOnboardingRoute } from "@/app/onboarding/lib/selectors";
import { OnboardingOverlay } from "@/app/onboarding-first-win/components/onboarding-overlay";
import { OnboardingOverlaySkeleton } from "@/app/onboarding-first-win/components/onboarding-overlay-skeleton";
import { OnboardingUnavailable } from "@/app/onboarding-first-win/components/onboarding-unavailable";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/onboarding"
)({
  // Not awaited: the page shows its skeleton while eligibility loads, and the
  // progress read starts as soon as eligibility allows it.
  loader: ({ params, context: { queryClient } }) => {
    prefetchOnboarding(queryClient, params).catch(() => undefined);
  },
  pendingComponent: OnboardingOverlaySkeleton,
  component: OnboardingRouteGate,
});

async function prefetchOnboarding(
  queryClient: QueryClient,
  { organizationId, projectId }: { organizationId: string; projectId: string }
) {
  const eligibility = await queryClient.ensureQueryData(
    eligibilityQueryOptions(organizationId, projectId)
  );
  if (canEnterOnboardingRoute(eligibility)) {
    await queryClient.prefetchQuery(
      onboardingProgressQueryOptions(organizationId, projectId)
    );
  }
}

function OnboardingRouteGate(): JSX.Element {
  const { organizationId, projectId } = Route.useParams();
  const { data: eligibility } = useSuspenseQuery(
    eligibilityQueryOptions(organizationId, projectId)
  );

  if (!canEnterOnboardingRoute(eligibility)) {
    return (
      <OnboardingUnavailable
        organizationId={organizationId}
        projectId={projectId}
      />
    );
  }

  return <OnboardingOverlay />;
}
